import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ArrowRight, Check } from "lucide-react";
import { useEffect, useState } from "react";
const getSupabase = () => import("@/integrations/supabase/client").then((m) => m.supabase);
import { lovable } from "@/integrations/lovable";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getFoundingPlaces } from "@/lib/pricing.functions";

export const Route = createFileRoute("/signup")({
  head: () => ({
    meta: [
      { title: "Claim your free place — Tiger's Eye" },
      {
        name: "description",
        content:
          "Join Tiger's Eye free as one of the first 49 students. Impartial academic critique of your essay or dissertation draft — never ghostwriting.",
      },
      { name: "robots", content: "noindex,follow" },
      { property: "og:title", content: "Claim your free place — Tiger's Eye" },
      {
        property: "og:description",
        content: "Join Tiger's Eye free as one of the first 49 students.",
      },
      { property: "og:url", content: "https://tigerseye.app/signup" },
    ],
    links: [{ rel: "canonical", href: "https://tigerseye.app/signup" }],
  }),
  component: SignupPage,
});

const BG = "var(--paper)";
const SURFACE = "var(--card)";
const INK = "var(--primary)";
const INK_SOFT = "var(--ink-secondary)";
const INK_FAINT = "var(--ink-tertiary)";
const BRASS = "var(--editorial-accent, var(--accent))";
const RULE = "var(--rule)";

const SERIF =
  '"Iowan Old Style", "Source Serif Pro", "Source Serif 4", "Charter", Georgia, ui-serif, serif';

function canonicalOrigin() {
  if (typeof window === "undefined") return "https://tigerseye.app";
  const host = window.location.hostname;
  if (host === "tigerseye.app" || host === "www.tigerseye.app") {
    return "https://tigerseye.app";
  }
  if (host === "tigerseye.lovable.app") {
    return "https://tigerseye.lovable.app";
  }
  return window.location.origin;
}

export default function SignupPage() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [places, setPlaces] = useState<{ claimed: number; total: number } | null>(null);

  useEffect(() => {
    if (!loading && user) navigate({ to: "/dashboard" });
  }, [user, loading, navigate]);

  useEffect(() => {
    let cancelled = false;
    getFoundingPlaces()
      .then((result) => {
        if (!cancelled) setPlaces(result);
      })
      .catch(() => {
        if (!cancelled) setPlaces(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleGoogle = async () => {
    const result = await lovable.auth.signInWithOAuth("google", {
      redirect_uri: `${canonicalOrigin()}/auth/callback`,
    });
    if (result.error) {
      toast.error(result.error.message ?? "Google sign-in failed");
      return;
    }
    if (result.redirected) return;
    navigate({ to: "/auth/callback" });
  };

  const handleEmailSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    const supabase = await getSupabase();
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${canonicalOrigin()}/auth/callback`,
        data: { display_name: displayName || email.split("@")[0] },
      },
    });
    setSubmitting(false);
    if (error) return toast.error(error.message);
    toast.success("Check your email to verify your account.");
  };

  return (
    <main className="min-h-screen w-full" style={{ background: BG, color: INK }}>
      <style>{`
        .hv4-serif { font-family: ${SERIF}; font-feature-settings: "liga","kern"; }
        .hv4-eyebrow {
          font-size: 11px; letter-spacing: 0.18em; text-transform: uppercase;
          font-weight: 600; color: ${BRASS};
        }
      `}</style>

      <header style={{ borderBottom: `1px solid ${RULE}` }}>
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Link to="/" className="hv4-serif text-lg font-semibold" style={{ color: INK }}>
            Tiger&apos;s Eye
          </Link>
          <Link to="/login" className="text-sm" style={{ color: INK_SOFT }}>
            Already joined? Sign in
          </Link>
        </div>
      </header>

      <section className="mx-auto max-w-md px-6 pb-24 pt-14">
        <p className="hv4-eyebrow">Founding students</p>
        <h1 className="hv4-serif mt-4 text-balance text-4xl leading-tight">
          Claim your free place
        </h1>
        <p className="mt-4 text-sm leading-relaxed" style={{ color: INK_SOFT }}>
          {places
            ? `${places.claimed} of ${places.total} founding places claimed. `
            : ""}
          Join free as one of the first 49 students — after that it&apos;s
          £4.95 per term.
        </p>

        <ul className="mt-6 flex flex-col gap-2 text-sm" style={{ color: INK_SOFT }}>
          {[
            "Full critique of every draft you upload",
            "Checked against your brief, level and referencing style",
            "Critique only — your grade stays yours",
          ].map((item) => (
            <li key={item} className="flex items-start gap-2">
              <Check className="mt-0.5 h-4 w-4 shrink-0" style={{ color: BRASS }} />
              <span>{item}</span>
            </li>
          ))}
        </ul>

        <div
          className="mt-8 rounded-2xl p-6"
          style={{ background: SURFACE, border: `1px solid ${RULE}` }}
        >
          <Button onClick={handleGoogle} variant="outline" className="w-full">
            Continue with Google
          </Button>

          <div className="relative my-5 text-center">
            <span className="relative z-10 px-3 text-xs" style={{ background: SURFACE, color: INK_FAINT }}>
              or with email
            </span>
            <span className="absolute inset-x-0 top-1/2 border-t" style={{ borderColor: RULE }} />
          </div>

          <form onSubmit={handleEmailSignUp} className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="signup-name">Your name</Label>
              <Input
                id="signup-name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Alex Smith"
                autoComplete="name"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="signup-email">Email</Label>
              <Input
                id="signup-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@university.ac.uk"
                autoComplete="email"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="signup-password">Password</Label>
              <Input
                id="signup-password"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 6 characters"
                autoComplete="new-password"
              />
            </div>
            <Button type="submit" disabled={submitting} className="mt-1 w-full">
              {submitting ? "Creating your account…" : "Create my free account"}
              <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </form>
        </div>

        <p className="mt-6 text-center text-xs leading-relaxed" style={{ color: INK_FAINT }}>
          We&apos;ll email you a verification link. Tiger&apos;s Eye critiques your
          own writing — it never writes or rewrites your work.
        </p>
      </section>
    </main>
  );
}
